import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  DealSpecificProperty,
  DealSpecificSchema,
  InvestmentDetailsInterface,
  SelectData,
} from "../../../../../models";
import { updateDeal } from "../../../../../service/fund";
import { UpdateDealRequest } from "../../../../../service/fund/Types";
import {
  GetDealResponse,
  searchCollaborators,
  SearchCollaboratorsResponse,
  searchDealSpecificSchemas,
  SearchDealSpecificSchemasResponse,
} from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import {
  camelCaseToSentenceCase,
  getDealStructureValidationErrorFieldKeys,
  hasDealStructureValidationError,
  jsonReviver,
  sentenceOrPascalToCamel,
} from "../../../../../utils";
import {
  buildDealStructureItems,
  getRelatedObjectDisplayName,
  mapInvestmentsToDealSpecificData,
} from "./utils/dealStructureUtils";

export type DealStructureListItem = {
  key: string;
  title: string;
  subtitle: string;
};

export type CollaboratorDetails = SearchCollaboratorsResponse["results"][number];
export type ProjectDetails = NonNullable<GetDealResponse["relatedObjects"][number]["project"]>;
export type LandholdingDetails = GetDealResponse["relatedObjects"][number]["landholding"];

interface UseDealStructureReturnData {
  errors: ServiceError[] | undefined;
  isEditing: boolean;
  isSaving: boolean;
  instrumentSelectData: SelectData;
  associationSelectData: SelectData;
  hasValidationError: (investmentKey: string | undefined, fieldKey: string) => boolean;
  selectedInvestmentKey: string | undefined;
  setSelectedInvestmentKey: Dispatch<SetStateAction<string | undefined>>;
  dealStructureItems: DealStructureListItem[];
  selectedInvestment: InvestmentDetailsInterface | undefined;
  dealSpecificSchemaProperties: [string, DealSpecificProperty][] | undefined;
  selectedCompany: CollaboratorDetails | undefined;
  selectedProjects: ProjectDetails[];
  selectedLandholdings: LandholdingDetails[];
  onUpdateInvestment: <K extends keyof InvestmentDetailsInterface>(
    key: string,
    field: K,
    value:
      | InvestmentDetailsInterface[K]
      | ((currentValue: InvestmentDetailsInterface[K]) => InvestmentDetailsInterface[K])
  ) => void;
  onRemoveInvestment: (key: string) => void;
  onAddInvestment: () => void;
  startEditing: () => void;
  cancelEditing: () => void;
  saveDealStructure: () => Promise<void>;
}

export const useDealStructure = (
  deal: GetDealResponse | undefined,
  onDealStructureSaved: () => Promise<void>
): UseDealStructureReturnData => {
  const [errors, setErrors] = useState<ServiceError[]>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedInvestmentKey, setSelectedInvestmentKey] = useState<string | undefined>(undefined);
  const [investments, setInvestments] = useState<InvestmentDetailsInterface[]>([]);
  const [savedInvestments, setSavedInvestments] = useState<InvestmentDetailsInterface[]>([]);
  const [dealRowVersion, setDealRowVersion] = useState<number | undefined>(deal?.rowVersion);
  const [collaborator, setCollaborator] = useState<CollaboratorDetails>();
  const [dealSpecificSchemasData, setDealSpecificSchemasData] =
    useState<SearchDealSpecificSchemasResponse["results"]>();

  const relatedObjects = deal?.relatedObjects ?? [];

  const relatedObjectLookup = useMemo(() => {
    return new Map(relatedObjects.map((object) => [object.objectUuid, object]));
  }, [relatedObjects]);

  const selectedInvestment = useMemo(
    () => investments.find((investment) => investment.key === selectedInvestmentKey),
    [investments, selectedInvestmentKey]
  );
  const errorFieldKeys = useMemo(
    () => getDealStructureValidationErrorFieldKeys(errors, investments),
    [errors, investments]
  );

  const dealSpecificSchemaProperties = useMemo(() => {
    if (!selectedInvestment) return undefined;

    const schema = (dealSpecificSchemasData ?? []).find(
      (s) => sentenceOrPascalToCamel(s.dealType) === selectedInvestment.instrument
    );

    if (!schema) return undefined;

    const definition = JSON.parse(schema.definition, jsonReviver) as DealSpecificSchema | undefined;

    return definition?.properties ? Object.entries(definition.properties) : undefined;
  }, [dealSpecificSchemasData, selectedInvestment]);

  const instrumentSelectData = useMemo<SelectData>(() => {
    return (dealSpecificSchemasData ?? []).map((schemaResult) => ({
      key: sentenceOrPascalToCamel(schemaResult.dealType),
      value: camelCaseToSentenceCase(schemaResult.dealType),
    }));
  }, [dealSpecificSchemasData, investments]);

  const associationSelectData = useMemo<SelectData>(() => {
    const unique = new Map(
      relatedObjects.map((obj) => [
        obj.objectUuid,
        getRelatedObjectDisplayName(obj.objectUuid, relatedObjectLookup, collaborator),
      ])
    );

    return Array.from(unique, ([key, value]) => ({ key, value }));
  }, [collaborator, relatedObjectLookup, relatedObjects]);

  const selectedRelatedObjects = useMemo(() => {
    if (!selectedInvestment) return [];

    const selectedObjectUuids = new Set(selectedInvestment.associations.map((x) => x));
    return relatedObjects.filter((relatedObject) => selectedObjectUuids.has(relatedObject.objectUuid));
  }, [relatedObjects, selectedInvestment]);

  const selectedProjects = useMemo(() => {
    return selectedRelatedObjects
      .filter((relatedObject) => relatedObject.objectType === "Project")
      .map((relatedObject) => relatedObject.project);
  }, [selectedRelatedObjects]);

  const selectedLandholdings = useMemo(() => {
    return selectedRelatedObjects
      .filter((relatedObject) => relatedObject.objectType === "Landholding")
      .map((relatedObject) => relatedObject.landholding);
  }, [selectedProjects, selectedRelatedObjects]);

  const selectedCompany = useMemo(() => {
    if (!collaborator) return undefined;
    if (!selectedInvestment?.associations.includes(collaborator.uuid)) return undefined;
    return collaborator;
  }, [collaborator, selectedInvestment?.associations]);

  const collaboratorObjectUuid = useMemo(() => {
    return relatedObjects.find((relatedObject) => relatedObject.objectType === "Collaborator")?.objectUuid;
  }, [relatedObjects]);

  // Items for deal strucutre
  const dealStructureItems = useMemo(
    () =>
      investments.map((investment) => {
        const relatedObjectNames = investment.associations
          .map((relatedObjectUuid) => getRelatedObjectDisplayName(relatedObjectUuid, relatedObjectLookup, collaborator))
          .join(", ");

        return {
          key: investment.key,
          title: camelCaseToSentenceCase(investment.instrument ?? "newInvestment"),
          subtitle: relatedObjectNames || "--",
        };
      }),
    [collaborator, investments, relatedObjectLookup]
  );

  const onUpdateInvestment: UseDealStructureReturnData["onUpdateInvestment"] = (key, field, value) => {
    setInvestments((previousInvestments) =>
      previousInvestments.map((investment) => {
        if (investment.key !== key) {
          return investment;
        }

        const currentValue = investment[field];
        const nextValue = typeof value === "function" ? value(currentValue) : value;

        return {
          ...investment,
          [field]: nextValue,
        };
      })
    );
  };

  const onRemoveInvestment: UseDealStructureReturnData["onRemoveInvestment"] = (key) => {
    setInvestments((previousInvestments) => previousInvestments.filter((investment) => investment.key !== key));
  };

  const onAddInvestment: UseDealStructureReturnData["onAddInvestment"] = () => {
    const newInvestment: InvestmentDetailsInterface = {
      key: uuidv4(),
      instrument: undefined,
      associations: [],
      description: undefined,
      fields: undefined,
    };

    setInvestments((previousInvestments) => [...previousInvestments, newInvestment]);
    setSelectedInvestmentKey(newInvestment.key);
  };

  const startEditing = (): void => {
    setIsEditing(true);
  };

  const cancelEditing = (): void => {
    setErrors(undefined);
    setInvestments(structuredClone(savedInvestments));
    setIsEditing(false);
  };

  // Save changes
  const saveDealStructure: UseDealStructureReturnData["saveDealStructure"] = async () => {
    if (!deal) return;

    setIsSaving(true);

    try {
      const nextDealSpecificData = mapInvestmentsToDealSpecificData(investments);

      const request: UpdateDealRequest = {
        conversionProbability: deal.conversionProbability,
        dealDates: JSON.parse(deal.dealDates),
        dealSpecificData: nextDealSpecificData,
        dealTeam: deal.usersInvolved?.map((user) => user.uuid) ?? [],
        dealUuid: deal.uuid,
        description: deal.description ?? "",
        estimatedFees: deal.estimatedFees ?? 0,
        introductionDate: deal.introductionDate ?? null,
        IsTemplateSelected: undefined,
        leadUserUuid: deal.leadUser?.uuid ?? "",
        name: deal.name,
        projectedDpi: deal.projectedDpi ?? null,
        relatedObjects:
          deal.relatedObjects?.map((relatedObject) => ({
            objectType: relatedObject.objectType,
            objectUuid: relatedObject.objectUuid,
          })) ?? [],
        rowVersion: dealRowVersion ?? deal.rowVersion,
        source: deal.source ?? null,
        stage: deal.stage,
        status: deal.status,
        targetCloseDate: deal.targetCloseDate ?? null,
      };

      const response = await updateDeal(request);

      if (response.status === Status.Success) {
        setErrors(undefined);
        await onDealStructureSaved();
        setIsEditing(false);
      }

      if (response.status === Status.Error) {
        setErrors(response.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Set investments based on saved dealSpecificData
  useEffect(() => {
    if (!deal?.dealSpecificData) {
      setInvestments([]);
      setSavedInvestments([]);
      setIsEditing(false);
      return;
    }

    const nextInvestments = [
      ...buildDealStructureItems("equity", deal.dealSpecificData.equity),
      ...buildDealStructureItems("landAcquisition", deal.dealSpecificData.landAcquisition),
      ...buildDealStructureItems("revenueShare", deal.dealSpecificData.revenueShare),
      ...buildDealStructureItems("unitPurchase", deal.dealSpecificData.unitPurchase),
      ...buildDealStructureItems("loan", deal.dealSpecificData.loan),
    ];

    setInvestments(nextInvestments);
    setSavedInvestments(structuredClone(nextInvestments));
    setIsEditing(false);
  }, [deal?.dealSpecificData]);

  useEffect(() => {
    setDealRowVersion(deal?.rowVersion);
  }, [deal?.rowVersion, deal?.uuid]);

  useEffect(() => {
    setSelectedInvestmentKey((currentKey) => {
      if (investments.length === 0) return undefined;
      if (currentKey && investments.some((investment) => investment.key === currentKey)) return currentKey;
      return investments[0].key;
    });
  }, [investments]);

  useEffect(() => {
    let isActive = true;

    if (!collaboratorObjectUuid) {
      setCollaborator(undefined);
      return () => {
        isActive = false;
      };
    }

    const loadCollaborator = async (): Promise<void> => {
      const response = await searchCollaborators({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 1,
        },
        filter: {
          results: {
            uuid: {
              operator: "eq",
              value: collaboratorObjectUuid,
            },
          },
        },
      });

      if (!isActive) return;

      if (response.status !== Status.Success || !response.data?.results?.[0]) {
        setCollaborator(undefined);
        return;
      }

      setCollaborator(response.data.results[0]);
    };

    loadCollaborator().catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [collaboratorObjectUuid]);

  useEffect(() => {
    const loadDealSpecificSchemas = async (): Promise<void> => {
      const response = await searchDealSpecificSchemas({
        paging: {
          afterCursor: null,
          beforeCursor: null,
          limit: 25,
        },
        sort: [{ key: "results.dealType", direction: "asc" }],
      });

      if (response.status === Status.Success && response.data?.results) {
        setDealSpecificSchemasData(response.data.results);
      }
    };

    loadDealSpecificSchemas();
  }, []);

  return {
    errors,
    isEditing,
    isSaving,
    instrumentSelectData,
    associationSelectData,
    hasValidationError: (investmentKey: string | undefined, fieldKey: string) =>
      hasDealStructureValidationError(errorFieldKeys, investmentKey, fieldKey),
    selectedInvestmentKey,
    setSelectedInvestmentKey,
    dealStructureItems,
    selectedInvestment,
    dealSpecificSchemaProperties,
    selectedCompany,
    selectedProjects,
    selectedLandholdings,
    onUpdateInvestment,
    onRemoveInvestment,
    onAddInvestment,
    startEditing,
    cancelEditing,
    saveDealStructure,
  };
};
